import { useEffect } from 'react';
import { useParams } from 'react-router-dom';

import { useDispatch, useSelector } from 'store';
import { setRfpData } from 'store/rfp';
import { setRfpResponseData } from 'store/rfpResponse';
import { useRfp, useRfpResponse } from 'api';
import { useSystemSettings } from 'providers/SystemSettingsProvider';

const useDataLoader = (rfpOnly?: boolean) => {
  const dispatch = useDispatch();
  const isLoggedIn = useSelector((store) => store.auth.isLoggedIn);
  const { unauthenticated_access } = useSystemSettings();

  if (!isLoggedIn && !unauthenticated_access) {
    throw new Error('User is not logged in');
  }

  const { rfpId, rfpResponseId } = useParams<{ rfpId: string; rfpResponseId: string }>();

  const { data: rfp, isLoading: isRfpLoading, error: rfpError } = useRfp(Number(rfpId));
  const {
    data: rfpResponse,
    isLoading: isRfpResponseLoading,
    error: rfpResponseError,
  } = useRfpResponse(rfpOnly ? undefined : Number(rfpResponseId));

  const reduxRfpId = useSelector((state) => state.rfp.data.id);
  const reduxRfpResponseId = useSelector((state) => state.rfpResponse.data.id);

  useEffect(() => {
    if (rfp !== undefined && Number(rfpId) === rfp.id) {
      dispatch(setRfpData(rfp));

      if (!rfpOnly && rfpResponse !== undefined && Number(rfpResponseId) === rfpResponse?.id) {
        dispatch(setRfpResponseData({ rfp, rfpResponse }));
      }
    }
  }, [dispatch, rfp, rfpResponse, rfpId, rfpResponseId, rfpOnly]);

  const renderChildren =
    rfp !== undefined &&
    Number(rfpId) === rfp.id &&
    Number(rfpId) === reduxRfpId &&
    (rfpOnly ||
      (rfpResponse !== undefined &&
        Number(rfpResponseId) === rfpResponse?.id &&
        Number(rfpResponseId) === reduxRfpResponseId));

  return [isRfpLoading || isRfpResponseLoading, rfpError ?? rfpResponseError, renderChildren] as const;
};

export default useDataLoader;
